import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, Chargement, MessageErreur } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { useCopieTest, useCorrigerTest, type CopieTest } from '../../lib/useTestsPositionnement';
import { useDemanderModification } from '../../lib/useModificationsSensibles';
import {
  AvisValidation,
  ChampMotif,
  DemandeEnCours,
  motifValide,
  useValidationDirecte,
} from '../modifications-sensibles/ElementsDemande';

function jour(valeur: string | null): string {
  return valeur ? new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' }) : '—';
}

/**
 * Notes de départ : celles déjà posées, à défaut la proposition de l'IA — à
 * valider, jamais enregistrée sans qu'une personne clique.
 */
function notesInitiales(copie: CopieTest): Record<string, { points: string; commentaire: string }> {
  const notes: Record<string, { points: string; commentaire: string }> = {};
  for (const question of copie.questions) {
    if (question.type !== 'REPONSE_LIBRE') continue;
    const ligne = copie.correction[question.id];
    const points = ligne?.points ?? ligne?.proposition?.points ?? null;
    notes[question.id] = {
      points: points === null ? '' : String(points),
      commentaire: ligne?.commentaire ?? '',
    };
  }
  return notes;
}

function Correction({ copie, onFermer }: { copie: CopieTest; onFermer: () => void }): JSX.Element {
  const notifier = useToast();
  const corriger = useCorrigerTest();
  const demander = useDemanderModification();
  const directe = useValidationDirecte();
  const [notes, setNotes] = useState(() => notesInitiales(copie));
  const [commentaire, setCommentaire] = useState(copie.commentaire ?? '');
  const [motif, setMotif] = useState('');

  const libres = copie.questions.filter((question) => question.type === 'REPONSE_LIBRE');
  const incomplet = libres.some((question) => (notes[question.id]?.points ?? '') === '');
  // Corrigé, le test a sa note lue par l'apprenant : la reprendre est une
  // demande à la direction (TF-437). Sans réponse libre, rien ne se reprend.
  const recorrection = copie.corrigeLe !== null;
  const enAttente = copie.demandeEnAttente;
  const saisieOuverte = !enAttente && libres.length > 0;

  const notesSaisies = Object.fromEntries(
    libres.map((question) => [
      question.id,
      {
        points: Number(notes[question.id]?.points ?? 0),
        ...(notes[question.id]?.commentaire.trim() ? { commentaire: notes[question.id]?.commentaire.trim() } : {}),
      },
    ]),
  ) as Record<string, { points: number; commentaire?: string }>;

  function envoyer(): void {
    if (recorrection) {
      demander.mutate(
        {
          type: 'NOTE_TEST',
          testId: copie.id,
          notes: notesSaisies,
          ...(commentaire.trim() ? { commentaire: commentaire.trim() } : {}),
          motif: motif.trim(),
        },
        {
          onSuccess: (resultat) => {
            notifier(resultat.message);
            onFermer();
          },
        },
      );
      return;
    }
    corriger.mutate(
      {
        testId: copie.id,
        notes: notesSaisies,
        ...(commentaire.trim() ? { commentaire: commentaire.trim() } : {}),
      },
      {
        onSuccess: (corrigee) => {
          notifier(`Correction enregistrée : ${corrigee.score}/100.`);
          onFermer();
        },
      },
    );
  }

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-codialis-gray">
        <span>Remis le {jour(copie.soumisLe)}</span>
        <span>{copie.modePassage === 'ESPACE' ? 'Passé dans l’espace apprenant' : 'Passé sur la tablette du centre'}</span>
        {copie.corrigeLe && (
          <span>
            Corrigé le {jour(copie.corrigeLe)}
            {copie.corrigePar ? ` par ${copie.corrigePar}` : ' d’office'} · {copie.score}/100
          </span>
        )}
      </div>

      {libres.length > 0 && !copie.correcteur.actif && (
        <p className="rounded-xl bg-codialis-sand px-4 py-3 text-xs text-codialis-navy">
          Aucune correction assistée n’est raccordée : les réponses libres se notent ici, à la main.
        </p>
      )}

      <ol className="space-y-3">
        {copie.questions.map((question, rang) => {
          const reponse = copie.reponses[question.id] ?? {};
          const ligne = copie.correction[question.id];
          return (
            <li key={question.id} className="space-y-2 rounded-2xl border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="min-w-0 flex-1 text-sm font-semibold text-codialis-navy">
                  {rang + 1}. {question.enonce}
                </p>
                <span className="shrink-0 text-xs text-codialis-gray">
                  {question.type === 'REPONSE_LIBRE'
                    ? `sur ${question.points}`
                    : `${ligne?.points ?? 0}/${question.points} · noté d’office`}
                </span>
              </div>

              {question.type === 'REPONSE_LIBRE' ? (
                <>
                  <p className="whitespace-pre-wrap rounded-xl bg-gray-50 px-3 py-2 text-sm text-codialis-navy">
                    {(reponse.texte ?? '').trim() || 'Sans réponse.'}
                  </p>
                  {question.attendu && (
                    <p className="text-[11px] text-codialis-gray">Attendu : {question.attendu}</p>
                  )}
                  {ligne?.proposition && (
                    <p className="rounded-lg bg-sky-50 px-3 py-2 text-[11px] text-codialis-navy">
                      Proposition {copie.correcteur.nom ? `de ${copie.correcteur.nom}` : 'assistée'} :{' '}
                      {ligne.proposition.points}/{question.points} — {ligne.proposition.justification}
                    </p>
                  )}
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-[8rem_1fr]">
                    <label className="block">
                      <span className="sr-only">Note de la question {rang + 1}</span>
                      <input
                        type="number"
                        min={0}
                        max={question.points}
                        value={notes[question.id]?.points ?? ''}
                        onChange={(e) =>
                          setNotes((courantes) => ({
                            ...courantes,
                            [question.id]: { commentaire: '', ...courantes[question.id], points: e.target.value },
                          }))
                        }
                        disabled={!saisieOuverte}
                        placeholder={`/ ${question.points}`}
                        className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy"
                      />
                    </label>
                    <label className="block">
                      <span className="sr-only">Commentaire de la question {rang + 1}</span>
                      <input
                        value={notes[question.id]?.commentaire ?? ''}
                        onChange={(e) =>
                          setNotes((courantes) => ({
                            ...courantes,
                            [question.id]: { points: '', ...courantes[question.id], commentaire: e.target.value },
                          }))
                        }
                        maxLength={1000}
                        disabled={!saisieOuverte}
                        placeholder="Commentaire (facultatif)"
                        className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy"
                      />
                    </label>
                  </div>
                </>
              ) : (
                <ul className="space-y-1">
                  {question.choix.map((choix) => {
                    const coche = (reponse.choix ?? []).includes(choix.id ?? '');
                    return (
                      <li
                        key={choix.id}
                        className={`flex items-center gap-2 text-sm ${coche ? 'font-semibold text-codialis-navy' : 'text-codialis-gray'}`}
                      >
                        {choix.correct ? (
                          <Check className="h-4 w-4 shrink-0 text-emerald-700" aria-label="Bonne réponse" />
                        ) : (
                          <X className="h-4 w-4 shrink-0 text-gray-300" aria-hidden />
                        )}
                        <span className="min-w-0">
                          {choix.libelle}
                          {coche ? ' · coché' : ''}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ol>

      <label className="block">
        <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
          Appréciation générale (facultative)
        </span>
        <textarea
          value={commentaire}
          onChange={(e) => setCommentaire(e.target.value)}
          rows={2}
          maxLength={2000}
          disabled={!saisieOuverte}
          className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy"
        />
      </label>

      {enAttente && <DemandeEnCours demande={enAttente} />}
      {recorrection && saisieOuverte && (
        <>
          <ChampMotif
            valeur={motif}
            onChange={setMotif}
            directe={directe}
            placeholder="Barème mal appliqué, réponse relue…"
          />
          <AvisValidation directe={directe} />
        </>
      )}

      {corriger.error instanceof ErreurApi && <MessageErreur message={corriger.error.message} />}
      {demander.error instanceof ErreurApi && <MessageErreur message={demander.error.message} />}

      <div className="flex flex-wrap gap-2">
        {saisieOuverte && (
          <BoutonPrincipal
            onClick={envoyer}
            disabled={corriger.isPending || demander.isPending || incomplet || (recorrection && !motifValide(motif))}
          >
            {corriger.isPending || demander.isPending
              ? 'Enregistrement…'
              : !recorrection
                ? 'Enregistrer la correction'
                : directe
                  ? 'Appliquer la nouvelle correction'
                  : 'Demander une nouvelle correction'}
          </BoutonPrincipal>
        )}
        <BoutonSecondaire onClick={onFermer} disabled={corriger.isPending || demander.isPending}>
          Fermer
        </BoutonSecondaire>
      </div>
    </div>
  );
}

/** Copie d'un test remis : réponses, corrigé, et notes des réponses libres. */
export function ModaleCorrection({ testId, onFermer }: { testId: string; onFermer: () => void }): JSX.Element {
  const { data, isLoading, error } = useCopieTest(testId);

  return (
    <Modale
      titre={data ? data.titre : 'Copie du test'}
      sousTitre={data ? `${data.apprenant}${data.niveau ? ` · ${data.niveau}` : ''}` : undefined}
      ouverte
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      {isLoading && <Chargement libelle="Chargement de la copie…" />}
      {error instanceof ErreurApi && (
        <div className="p-4 sm:p-6">
          <MessageErreur message={error.message} />
        </div>
      )}
      {data && <Correction key={data.id} copie={data} onFermer={onFermer} />}
    </Modale>
  );
}
